import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="home-nav">
        <div className="nav-content">
          <div className="logo">
            <img className="logo-icon" src="/bestie_logo.png" alt="Bestie" />
            <span className="logo-text">Bestie Connect</span>
          </div>
          <div className="nav-links">
            <Link to="/login" className="nav-link">Login</Link>
            <Link to="/register" className="nav-cta">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">No judgment. No pressure. Just support.</div>
          <h1 className="hero-title">
            Your Life. <span className="gradient-text">Organized.</span><br />
            <span className="gradient-text">Supported.</span> Understood.
          </h1>
          <p className="hero-subtitle">
            BestieConnect is a human-first platform combining task planning, addiction recovery support, 
            meaningful connection, and people-centered solutions for individuals and teams.
          </p>
          <div className="hero-ctas">
            <Link to="/register" className="btn-primary">
              Get Started Free
              <span className="btn-icon">→</span>
            </Link>
            <button className="btn-secondary" onClick={() => document.getElementById('enterprise').scrollIntoView({ behavior: 'smooth' })}>
              For Teams
            </button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-card card-1">
            <div className="card-icon">✓</div>
            <div className="card-text">Task Planning</div>
          </div>
          <div className="floating-card card-2">
            <div className="card-icon">🌱</div>
            <div className="card-text">Recovery Support</div>
          </div>
          <div className="floating-card card-3">
            <div className="card-icon">👥</div>
            <div className="card-text">Buddy System</div>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="what-is-section">
        <div className="section-content">
          <h2 className="section-title">What is Bestie Connect?</h2>
          <div className="what-is-grid">
            <div className="what-is-text">
              <p className="large-text">
                BestieConnect is <strong>not just productivity.</strong><br />
                Not just recovery.<br />
                Not just community.
              </p>
              <p className="highlight-text">
                It's a structured support system designed for real people navigating real life.
              </p>
            </div>
            <div className="what-is-list">
              <p>Whether you're trying to:</p>
              <ul>
                <li>Stay consistent with your goals</li>
                <li>Break addictive habits</li>
                <li>Feel less alone</li>
                <li>Support your employees better</li>
              </ul>
              <p className="conclusion">
                BestieConnect gives you tools that are <strong>practical, personal, and built for growth.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-content">
          <div className="section-header">
            <span className="section-badge">👤 FOR INDIVIDUALS</span>
            <h2 className="section-title">Built for Real People</h2>
          </div>

          <div className="features-grid">
            {/* Feature 1: Task */}
            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-number">1️⃣</div>
                <h3>Bestie Task</h3>
              </div>
              <p className="feature-tagline">Plan your life without feeling overwhelmed.</p>
              <p className="feature-description">
                Bestie Task is a structured task planning system built for mental clarity.
              </p>
              <ul className="feature-list">
                <li>Smart task organization</li>
                <li>Daily structure builder</li>
                <li>Gentle reminders</li>
                <li>Progress tracking</li>
                <li>Focus mode for deep work</li>
              </ul>
              <p className="feature-note">
                This isn't just a to-do list. It's task planning designed around how humans actually think and behave.
              </p>
              <Link to="/register" className="feature-cta">Start Planning Smarter →</Link>
            </div>

            {/* Feature 2: Recovery */}
            <div className="feature-card featured">
              <div className="feature-header">
                <div className="feature-number">2️⃣</div>
                <h3>Addiction Recovery</h3>
              </div>
              <p className="feature-tagline">Support without stigma. Progress without shame.</p>
              <p className="feature-description">
                Recovery is not linear. It's personal.
              </p>
              <ul className="feature-list">
                <li>Private recovery tracking</li>
                <li>Daily reflection prompts</li>
                <li>Trigger awareness logging</li>
                <li>Milestone tracking</li>
                <li>Gentle accountability</li>
              </ul>
              <p className="feature-note">
                No public exposure. No toxic comparison. Just structured support that respects your journey.
              </p>
              <Link to="/register" className="feature-cta">Begin Recovery Support →</Link>
            </div>

            {/* Feature 3: Buddy */}
            <div className="feature-card">
              <div className="feature-header">
                <div className="feature-number">3️⃣</div>
                <h3>Bestie Buddy</h3>
              </div>
              <p className="feature-tagline">You don't have to do life alone.</p>
              <p className="feature-description">
                Bestie Buddy connects people through:
              </p>
              <ul className="feature-list">
                <li>Supportive accountability pairing</li>
                <li>Guided conversation prompts</li>
                <li>Encouragement check-ins</li>
                <li>Shared goal tracking</li>
              </ul>
              <p className="feature-note">
                Human connection improves consistency. This feature brings warmth into structure.
              </p>
              <Link to="/register" className="feature-cta">Find Your Buddy →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Donate Section */}
      <section className="donate-section">
        <div className="section-content">
          <div className="donate-content">
            <div className="donate-icon">💝</div>
            <h2>Support Someone Else's Journey</h2>
            <p className="donate-description">
              BestieConnect allows users and organizations to sponsor recovery access, 
              fund support memberships, and contribute to community assistance.
            </p>
            <p className="donate-note">This isn't charity for optics. It's community infrastructure.</p>
            <Link to="/register" className="btn-donate">Make an Impact →</Link>
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="enterprise-section" id="enterprise">
        <div className="section-content">
          <div className="section-header">
            <span className="section-badge">🏢 FOR ENTERPRISE / COMPANIES</span>
            <h2 className="section-title">People-First Productivity at Scale</h2>
          </div>

          <div className="enterprise-grid">
            <div className="enterprise-problems">
              <h3>Companies Struggle With:</h3>
              <ul>
                <li>Burnout</li>
                <li>Disengagement</li>
                <li>Silent addiction struggles</li>
                <li>Disconnected teams</li>
              </ul>
            </div>

            <div className="enterprise-solution">
              <h3>Enterprise Bestie Provides:</h3>
              <ul>
                <li>Team wellness dashboards (aggregated insights only)</li>
                <li>Structured productivity tools for employees</li>
                <li>Recovery resource access</li>
                <li>Accountability pairing options</li>
                <li>Leadership insight reports</li>
                <li>Employee engagement metrics</li>
              </ul>
              <div className="enterprise-note">
                <strong>What you don't get:</strong> Surveillance. Overreach. Micromanagement.
                <br />This is ethical workforce support.
              </div>
            </div>
          </div>

          <div className="enterprise-cta-section">
            <Link to="/register" className="btn-enterprise">Talk to Our Enterprise Team →</Link>
          </div>
        </div>
      </section>

      {/* How It Connects */}
      <section className="connection-section">
        <div className="section-content">
          <h2 className="section-title">How It All Connects</h2>
          <p className="connection-subtitle">BestieConnect works because it combines:</p>
          <div className="connection-grid">
            <div className="connection-item">
              <div className="connection-icon">📋</div>
              <div className="connection-label">Structure</div>
              <div className="connection-desc">Task Planning</div>
            </div>
            <div className="connection-item">
              <div className="connection-icon">🤝</div>
              <div className="connection-label">Support</div>
              <div className="connection-desc">Buddy System</div>
            </div>
            <div className="connection-item">
              <div className="connection-icon">🌱</div>
              <div className="connection-label">Healing</div>
              <div className="connection-desc">Addiction Recovery</div>
            </div>
            <div className="connection-item">
              <div className="connection-icon">💝</div>
              <div className="connection-label">Impact</div>
              <div className="connection-desc">Donations</div>
            </div>
            <div className="connection-item">
              <div className="connection-icon">📈</div>
              <div className="connection-label">Scale</div>
              <div className="connection-desc">Enterprise</div>
            </div>
          </div>
          <p className="connection-conclusion">
            It's not a feature stack. <strong>It's a life support framework.</strong>
          </p>
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section">
        <div className="section-content">
          <h2 className="section-title">Why Bestie Connect Exists</h2>
          <div className="why-grid">
            <div className="why-problem">Modern life is overwhelming.</div>
            <div className="why-problem">Productivity tools ignore mental health.</div>
            <div className="why-problem">Recovery tools ignore daily structure.</div>
            <div className="why-problem">Corporate tools ignore humanity.</div>
          </div>
          <p className="why-solution">
            BestieConnect integrates all three.<br />
            <strong>This is people-centered design.</strong>
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="section-content">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Individual</h3>
              <div className="price">Free</div>
              <p className="price-desc">Free Tier Available</p>
              <Link to="/register" className="pricing-cta">Get Started</Link>
            </div>
            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Premium</h3>
              <div className="price">Contact Us</div>
              <p className="price-desc">Advanced tools and buddy features</p>
              <Link to="/register" className="pricing-cta">Start Premium</Link>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">Custom</div>
              <p className="price-desc">Custom pricing for teams</p>
              <Link to="/register" className="pricing-cta">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="section-content">
          <h2 className="final-cta-title">
            Start building a better structure for your life — or your company.
          </h2>
          <div className="final-ctas">
            <Link to="/register" className="btn-final-primary">Get Started</Link>
            <Link to="/register" className="btn-final-secondary">Book Enterprise Demo</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="logo">
              <img className="logo-icon" src="/bestie_logo.png" alt="Bestie" />
              <span className="logo-text">Bestie Connect</span>
            </div>
            <p>People-centered design for real life.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <Link to="/register">Bestie Task</Link>
              <Link to="/register">Recovery Support</Link>
              <Link to="/register">Bestie Buddy</Link>
              <Link to="/register">Enterprise</Link>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <Link to="/register">About</Link>
              <Link to="/register">Pricing</Link>
              <Link to="/register">Contact</Link>
            </div>
            <div className="footer-column">
              <h4>Get Started</h4>
              <Link to="/login">Login</Link>
              <Link to="/register">Sign Up</Link>
            </div>
            <div className="footer-column">
              <h4>Contact Us</h4>
              <a href="mailto:info@bestieconnect.com" className="footer-contact">info@bestieconnect.com</a>
              <a href="mailto:support@bestieconnect.com" className="footer-contact">support@bestieconnect.com</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 BestieConnect. Built with care for real people.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
